import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Build as BuildIcon,
  Inventory2 as PartIcon,
  Search as SearchIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  WarningAmberRounded as WarningIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import type {
  ProductsListItem,
  RepairDeviceType,
  RepairService,
  RepairServiceComponent,
  RepairServiceType,
  Vendor,
} from '@techgitter/contracts';
import {
  DEFAULT_INCLUDES_CLOSING_LINE,
  HW_REPAIR_PART_SKU,
  RCS_REPAIR_LABOUR_SKU,
} from '@techgitter/contracts';
import { RepairServicePicker } from './RepairServicePicker';
import { InventoryPickerDialog } from './InventoryPickerDialog';
import {
  getProductBySku,
  useProductCatalog,
} from '../../hooks/useProductCatalog';
import { useRepairServiceTypes } from '../../hooks/useRepairServiceTypes';
import { cleanSupplierUrl } from '../../utils/cleanSupplierUrl';
import { evaluateMargin } from '../../utils/marginPricing';
import type { MarginVerdict } from '../../utils/marginPricing';
import { applyEft } from '../../utils/quoteEftPricing';

const COMPONENT_KINDS: { value: RepairServiceComponent['kind']; label: string }[] = [
  { value: 'labour', label: 'Labour' },
  { value: 'part', label: 'Part' },
  { value: 'third_party', label: 'Third party' },
  { value: 'fixed_price', label: 'Fixed price' },
];

const newId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const fmtCurrency = (n: number): string =>
  n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

/** Suggested lead-time labels shown in the third-party Lead time picker.
 *  Free-form input is still accepted so a vendor saying "5-7 weeks" can
 *  be typed in directly — `LEAD_TIME_FORMAT` validates the result. */
const LEAD_TIME_SUGGESTIONS = [
  '1-2 days',
  '2-3 days',
  '3-5 days',
  '1 week',
  '1-2 weeks',
  '2-3 weeks',
  '3-4 weeks',
  '4 weeks',
];

/** Accepted lead-time formats: `N days|weeks` or `N-M days|weeks`. */
const LEAD_TIME_FORMAT = /^\d+(-\d+)?\s*(days?|weeks?)$/i;

const isLeadTimeValid = (value: string | null | undefined): boolean => {
  if (value == null) return true;
  const trimmed = value.trim();
  if (trimmed === '') return true;
  return LEAD_TIME_FORMAT.test(trimmed);
};

/** Round to 2 decimal places. Every numeric input in the editor is
 *  rendered through this so we never display 163.6363636 — and every
 *  derived value persisted back into state is rounded the same way so
 *  storage matches what the tech sees. */
const round2 = (n: number): number =>
  Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;

/**
 * Sell-price input with local string state.
 *
 * The component renders the editable value as a string the user is
 * actively typing — so partial entries like "10.", "100.5", or even an
 * empty field don't get clobbered by the parent's round-tripped numeric
 * state (which previously rendered `value={round2(comp.sellPrice * 1.1)}`
 * and fought the user's keystrokes on every render).
 *
 * Behaviour:
 *  - `valueExGst` is the canonical ex-GST sell stored on the component.
 *  - When `gstInclusive` is true, the local string starts as that value
 *    × 1.1 (inc-GST display); typing then writes back the ex-GST
 *    equivalent via the supplied `onChange`.
 *  - If `valueExGst` changes from the outside (e.g. the tech swaps the
 *    productCode and the catalog price re-seeds the sell), the local
 *    string resyncs — but only when the *rounded* display value diverges
 *    from what's currently shown, so the user's in-flight typing is left
 *    alone.
 *  - `type="text"` + `inputMode="decimal"` removes the browser's number
 *    spinners (which had crept back via the default UA styling) while
 *    still showing a numeric keyboard on mobile.
 */
const SellPriceInput: React.FC<{
  /** Used as the input's accessible name; the visible column header now
   *  carries the label, so it is not rendered inline. */
  label: string;
  valueExGst: number;
  gstInclusive: boolean;
  disabled?: boolean;
  /** Tiny ex/inc suffix shown inside the field — parts price in their
   *  supplier's GST mode, which can differ from the quote's, so the tag
   *  keeps each Sell value unambiguous under a single "Sell" header. */
  gstTag?: string;
  onChange: (nextExGst: number) => void;
}> = ({ label, valueExGst, gstInclusive, disabled, gstTag, onChange }) => {
  const displayValue = gstInclusive ? valueExGst * 1.1 : valueExGst;
  const [text, setText] = useState<string>(() => String(round2(displayValue)));
  const lastRenderedRef = useRef<number>(round2(displayValue));

  useEffect(() => {
    const rounded = round2(displayValue);
    if (Math.abs(rounded - lastRenderedRef.current) > 0.005) {
      setText(String(rounded));
      lastRenderedRef.current = rounded;
    }
  }, [displayValue]);

  return (
    <TextField
      variant="standard"
      size="small"
      fullWidth
      aria-label={label}
      type="text"
      inputMode="decimal"
      value={text}
      inputProps={{ style: { textAlign: 'right' } }}
      InputProps={{
        endAdornment: gstTag ? (
          <Typography
            component="span"
            sx={{ fontSize: '0.625rem', color: 'text.disabled', ml: 0.5 }}
          >
            {gstTag}
          </Typography>
        ) : undefined,
        sx: { fontSize: '0.85rem', fontWeight: 600 },
      }}
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        // Empty or partial entries ("", "-", ".", "10.") are not
        // propagated — we only write a numeric sellPrice once the
        // string parses to a finite number. The local string keeps
        // showing what the user typed.
        const parsed = Number(next);
        if (next.trim() !== '' && Number.isFinite(parsed)) {
          // Keep precision on the inc→ex divide; round2-ing here
          // means an inc input that doesn't divide cleanly by 1.1
          // (e.g. 45 inc → 40.9090… ex) loses sub-cent precision,
          // which then re-multiplies to 45.005 and surfaces as
          // $225.01 on a row total that should read $225.00.
          // Direct-ex input is rounded normally — only the divide
          // path needs the extra precision.
          const ex = gstInclusive ? parsed / 1.1 : round2(parsed);
          lastRenderedRef.current = round2(
            gstInclusive ? ex * 1.1 : ex,
          );
          onChange(ex);
        }
      }}
      disabled={disabled}
    />
  );
};

const componentContributesToTotal = (c: RepairServiceComponent): boolean => {
  // Third-party rows are vendor-quote-comparison entries. Only the
  // `accepted` one is billed; everything else is internal tracking and
  // contributes $0 to the customer-facing total.
  if (c.kind === 'third_party') return c.vendorQuoteStatus === 'accepted';
  return true;
};

const serviceTotal = (svc: RepairService, gstInclusive: boolean): number => {
  const sum = (svc.components || []).reduce((acc, c) => {
    if (!componentContributesToTotal(c)) return acc;
    return acc + (Number.isFinite(c.sellPrice) ? c.sellPrice : 0);
  }, 0);
  return gstInclusive ? sum * 1.1 : sum;
};

/** Shared column track for the component table — header and every row use
 *  this exact template so cells line up into columns. Only the Item column
 *  flexes; the rest are fixed so alignment holds across rows. */
const GRID_TEMPLATE =
  '28px 116px minmax(160px, 1fr) 84px 132px 132px 96px 36px';

const fmtPct = (ratio: number): string => `${(ratio * 100).toFixed(1)}%`;

/** Per-kind chip styling for the Kind column. Colours echo the mockup so a
 *  manager can scan a service and tell labour / parts / vendor lines apart. */
const KIND_META: Record<
  RepairServiceComponent['kind'],
  { label: string; bg: string; fg: string }
> = {
  labour: { label: 'Labour', bg: '#e3f2fd', fg: '#1565c0' },
  part: { label: 'Part', bg: '#ede7f6', fg: '#5e35b1' },
  third_party: { label: '3rd party', bg: '#fff3e0', fg: '#e65100' },
  fixed_price: { label: 'Fixed', bg: '#e8f5e9', fg: '#2e7d32' },
};

/** True for the kinds that carry a separable cost we can take a margin on.
 *  Labour cost == sell (margin lives in the rate) and fixed-price has no
 *  cost, so both are excluded from profit/margin display and warnings.
 *  Stock parts (e.g. added from inventory) sell at the catalog price with
 *  no tracked wholesale cost — and the editor exposes no cost/sourcing row
 *  for them — so their "margin" is always a spurious 0%. Exclude them too,
 *  so they don't render a GP figure or trip the below-floor warning. */
const hasMargin = (c: RepairServiceComponent): boolean =>
  (c.kind === 'part' && c.fulfillmentSource !== 'stock') ||
  c.kind === 'third_party';

export interface ServiceProfit {
  /** Whether any margin-bearing (part / third-party) line exists. */
  hasData: boolean;
  costExGst: number;
  sellExGst: number;
  profitExGst: number;
  /** Blended margin across the margin-bearing lines, or null when no sell. */
  margin: number | null;
  /** Count of part lines below their cost-tier margin floor. */
  lowCount: number;
}

/** Aggregate cost / sell / profit across a service's part + accepted
 *  third-party lines. Labour and fixed-price are excluded (no separable
 *  cost). Returns ex-GST figures — callers flip to inc-GST for display. */
const serviceProfit = (svc: RepairService): ServiceProfit => {
  let costExGst = 0;
  let sellExGst = 0;
  const lowCount = 0;
  let hasData = false;
  for (const c of svc.components || []) {
    if (!hasMargin(c)) continue;
    // Mirror the total: only the accepted third-party line is billed.
    if (!componentContributesToTotal(c)) continue;
    hasData = true;
    const lineCost = computeBuyTotal(c);
    const lineSell = Number.isFinite(c.sellPrice) ? c.sellPrice : 0;
    costExGst += lineCost;
    sellExGst += lineSell;
    // Below-floor margin warnings are intentionally suppressed for repair-quote
    // parts (see the per-line note above), so lowCount stays 0 — the aggregate
    // "N lines below margin floor" chip / total never fires.
  }
  return {
    hasData,
    costExGst,
    sellExGst,
    profitExGst: sellExGst - costExGst,
    margin: sellExGst > 0 ? (sellExGst - costExGst) / sellExGst : null,
    lowCount,
  };
};

const effectiveUnits = (c: RepairServiceComponent): number =>
  Number.isFinite(c.units as number) && (c.units as number) > 0
    ? (c.units as number)
    : 1;

const effectiveUnitPrice = (c: RepairServiceComponent): number => {
  if (Number.isFinite(c.unitPriceExGst as number)) {
    return c.unitPriceExGst as number;
  }
  // Legacy fallback — pre-split rows stored the whole component as a flat
  // `buyPriceExGst`. Treat it as 1-unit at that price so the editor renders
  // a sensible breakdown until the row is re-saved.
  return Number.isFinite(c.buyPriceExGst) ? c.buyPriceExGst : 0;
};

const computeBuyTotal = (c: RepairServiceComponent): number => {
  const u = effectiveUnits(c);
  const r = effectiveUnitPrice(c);
  const ship = Number.isFinite(c.shipping) ? c.shipping : 0;
  // Intentionally unrounded — downstream sell math (third-party
  // `cost + markup`, and the inc-GST display round-trip) multiplies
  // before the final round2. Pre-rounding here was leaking a cent of
  // error into the sell price whenever shipping or unit cost came from
  // an inc-GST input that didn't divide cleanly by 1.1 (e.g. inc-15 →
  // ex-13.6363…, not 13.64). Display sites round the value at render
  // time via round2 on display.
  return u * r + ship;
};

/** Sell-from-cost for a third-party component. Reads the snapshotted
 *  markupValue stored on the component at add-time so an admin's later
 *  change to the RepairServiceType doesn't drift live quotes. Falls
 *  back to the raw cost when no markup is configured. */
const computeThirdPartySell = (c: RepairServiceComponent): number => {
  const cost = Number.isFinite(c.buyPriceExGst) ? c.buyPriceExGst : 0;
  const value = Number.isFinite(c.markupValue as number)
    ? (c.markupValue as number)
    : 0;
  if (c.markupType === 'fixed') return round2(cost + value);
  return round2(cost);
};

/** Catalog `unitCost` values are stored GST-inclusive. We keep
 *  component rates ex-GST internally (display flips to inc-GST when
 *  the quote is in inc-GST mode) so every catalog read goes through
 *  this conversion.
 *
 *  The divide is intentionally NOT rounded — mirroring every other
 *  inc→ex path in this editor. Rounding here loses sub-cent precision
 *  on rates that don't divide cleanly by 1.1 (e.g. $180 inc →
 *  163.6363… ex, not 163.64), and that cent re-surfaces once the rate
 *  is multiplied by hours/qty and flipped back to inc — e.g. a 1.5h
 *  labour line at $180/hr inc rendering $270.01 instead of $270.00.
 *  Display sites round at render time via round2. */
const catalogIncToExGst = (incGstPrice: number | undefined): number => {
  if (incGstPrice == null || !Number.isFinite(incGstPrice)) return 0;
  return incGstPrice / 1.1;
};

/** Per-hour labour rate sourced from the catalog and returned ex-GST.
 *  Defaults to the `RCS.RepairLabour` SKU but accepts any override sku
 *  so a tech who flips the productCode to e.g. `RCS.SeniorTechLabour`
 *  gets the new rate auto-populated. Falls back to 0 if the catalog
 *  hasn't loaded or the SKU is missing. */
const labourRateForSku = (sku?: string | null): number => {
  const product = getProductBySku(sku || RCS_REPAIR_LABOUR_SKU);
  return catalogIncToExGst(product?.unitCost);
};

const LABOUR_DEFAULT_DESCRIPTION = 'Repair service labour';

export interface RepairServicesEditorProps {
  services: RepairService[];
  deviceType?: RepairDeviceType;
  priceDisplayMode: 'ex' | 'inc';
  /**
   * EFT-fee buffer settings for this quote. When `includeEftFee` is on and
   * `eftFeeRate` is positive, the per-service subtotals and the grand
   * "Services total" show the EFT-inclusive price we quote the customer
   * (matching the preview + invoice). The uplift is a totals-only display
   * transform — per-component sell prices and the GP%/margin figures stay
   * pre-EFT, so the EFT amount never enters the margin calculation.
   */
  includeEftFee?: boolean;
  eftFeeRate?: number;
  /**
   * Full vendor list. The third-party vendor picker filters this down
   * to active providers with `isThirdPartyService === true`.
   */
  vendors?: Vendor[];
  onChange: (next: RepairService[]) => void;
  /**
   * Called when a service carrying a `defaultQuoteTermsTemplateId` is added
   * to the quote, with that template's slug. The parent decides whether to
   * apply it (first-service-wins, fill-if-empty) — this editor doesn't own
   * the quote's terms-template state.
   */
  onServiceDefaultTermsTemplate?: (slug: string) => void;
  disabled?: boolean;
  /**
   * Lock the editor to a single service with this RCS.* code (used by
   * network_install quotes, which always bill one "Network Installation"
   * line — it's not a device, so it doesn't map to the device-scoped
   * repair-service catalog). When set:
   *  - the service is auto-seeded from the catalog if the list is empty,
   *  - the per-service RepairServicePicker is replaced with a static label,
   *  - the per-service Remove button and the bottom "add service" picker
   *    are hidden.
   * The tech still edits the labour / part / fixed-price components under it.
   */
  lockedServiceCode?: string;
  /** Fallback display name used when auto-seeding and the catalog has no
   *  matching `lockedServiceCode` row. Ignored once a catalog match seeds
   *  the service's own name. */
  lockedServiceName?: string;
}

const VENDOR_QUOTE_STATUS_OPTIONS: {
  value: NonNullable<RepairServiceComponent['vendorQuoteStatus']>;
  label: string;
}[] = [
  { value: 'awaiting', label: 'Awaiting quote' },
  { value: 'received', label: 'Quote received' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
  { value: 'unable_to_quote', label: 'Unable to quote' },
];

/**
 * Editor for the customer-facing repair services on a Repair Service quote.
 * Each service is a card containing a picker (resolves to RCS.* SKU), an
 * editable description shown on the invoice, and a list of internal
 * labour / part components that drive the customer price.
 */
export const RepairServicesEditor: React.FC<RepairServicesEditorProps> = ({
  services,
  deviceType,
  priceDisplayMode,
  vendors = [],
  onChange,
  onServiceDefaultTermsTemplate,
  disabled = false,
  lockedServiceCode,
  lockedServiceName,
  includeEftFee = false,
  eftFeeRate = 0,
}) => {
  const gstInclusive = priceDisplayMode === 'inc';
  // EFT buffer is active when the flag is on AND the rate is positive.
  const eftActive = includeEftFee && eftFeeRate > 0;
  // Customer-facing totals include the EFT uplift; per-component prices and
  // margin figures do NOT (they read pre-EFT sellPrice).
  const withEft = (amount: number): number =>
    applyEft(amount, includeEftFee, eftFeeRate);
  // When `addService` seeds a part component, we capture its id here so
  // the next render's Source field for that component can grab focus.
  // Cleared by the inputRef callback after a single focus() — the tech
  // would expect to land on Source so they can pick Order vs. Stock
  // before doing anything else with the line.
  const [pendingFocusSourceId, setPendingFocusSourceId] = useState<
    string | null
  >(null);
  // Component ids whose cost/sourcing (or vendor-quote) detail row is
  // expanded. Detail is collapsed by default — the tech enters it once,
  // then it folds to a one-line summary so the table scans cleanly.
  const [expandedDetails, setExpandedDetails] = useState<Set<string>>(
    () => new Set(),
  );
  const toggleDetail = (componentId: string): void =>
    setExpandedDetails((prev) => {
      const next = new Set(prev);
      if (next.has(componentId)) next.delete(componentId);
      else next.add(componentId);
      return next;
    });

  // Drag-to-reorder state. Reordering is scoped to within a single service —
  // component ids are uuids so a single dragged/over pair is unambiguous; the
  // `end::{serviceId}` sentinel marks a drop at the bottom of a group.
  const [draggedCompId, setDraggedCompId] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);

  // When non-null, the inventory picker is open and its pick will be
  // appended as a new part component to this service.
  const [partPickerServiceId, setPartPickerServiceId] = useState<
    string | null
  >(null);
  // When true, the inventory picker is open in "standalone part" mode — its
  // pick becomes a new standalone-part entry (its own quote/invoice line)
  // rather than a component under an existing service.
  const [standalonePartPickerOpen, setStandalonePartPickerOpen] =
    useState(false);

  /** Move a component within its service. `targetId` of null drops it at the
   *  end. Unlike the work-item dashboard there's no displayIndex / API call —
   *  the order is just the array order, persisted with the rest of the quote
   *  via onChange. */
  const reorderComponents = (
    serviceId: string,
    sourceId: string,
    targetId: string | null,
  ): void => {
    if (sourceId === targetId) return;
    onChange(
      services.map((s) => {
        if (s.id !== serviceId) return s;
        const comps = [...s.components];
        const from = comps.findIndex((c) => c.id === sourceId);
        if (from === -1) return s;
        const [moved] = comps.splice(from, 1);
        if (targetId == null) {
          comps.push(moved);
        } else {
          const to = comps.findIndex((c) => c.id === targetId);
          if (to === -1) comps.push(moved);
          else comps.splice(to, 0, moved);
        }
        return { ...s, components: comps };
      }),
    );
  };

  const handleComponentDrop = (
    svcId: string,
    targetId: string | null,
  ): void => {
    const src = draggedCompId;
    setDraggedCompId(null);
    setDragOverKey(null);
    if (!src) return;
    // Same-service only: ignore a drop whose source lives in another service.
    const svc = services.find((s) => s.id === svcId);
    if (!svc || !svc.components.some((c) => c.id === src)) return;
    reorderComponents(svcId, src, targetId);
  };
  // Vendors available for the third-party picker. Tech can still select
  // a previously-snapshotted vendor that no longer matches the filter
  // (e.g. we removed their third-party flag) — handled at render-time.
  const thirdPartyVendorPool = useMemo(
    () =>
      vendors.filter(
        (v) => v.isActive && v.isThirdPartyService,
      ),
    [vendors],
  );
  // Vendors available for the part-supplier picker — anything we buy
  // parts from. Distis (ex-GST priced) and retailers (inc-GST priced).
  // Picked vendor's `isRetailer` seeds the line's `supplierGstMode`.
  const partVendorPool = useMemo(
    () =>
      vendors.filter(
        (v) => v.isActive && (v.isDisti || v.isRetailer),
      ),
    [vendors],
  );

  // Drive the labour-rate default off the live `products` list rather than
  // the synchronous `getProductBySku` lookup. That lookup reads a
  // module-level Map that's only populated AFTER `useProductCatalog`
  // resolves — so anything calling it from the editor's render cycle
  // before the catalog is loaded (i.e. on first paint and during the
  // user clicking "Add labour" before the load completes) gets back
  // undefined and seeds rows with rate 0. Reading from the products
  // array is reactive — once it populates, the memo recomputes and
  // the backfill effect below patches existing rows.
  const { products } = useProductCatalog();
  // Repair-service catalog — only consumed when `lockedServiceCode` is set,
  // to resolve the locked service's catalog row for auto-seeding. The shared
  // hook caches across the picker + editor, so this is the same fetch the
  // RepairServicePicker already uses.
  const { types: serviceTypes, isLoading: serviceTypesLoading } =
    useRepairServiceTypes();
  const catalogLabourRate = useMemo(() => {
    if (!products || products.length === 0) return 0;
    const p = products.find((x) => x.sku === RCS_REPAIR_LABOUR_SKU);
    // Catalog `unitCost` is inc-GST — convert before storing as our
    // canonical ex-GST `unitPriceExGst`.
    return catalogIncToExGst(p?.unitCost);
  }, [products]);

  // Whenever the catalog rate becomes available, populate any labour row
  // whose stored `unitPriceExGst` is 0 — those rows were either seeded
  // before the catalog loaded, or saved that way historically. We treat
  // 0 as "unset"; once the row has a positive rate the tech can edit it
  // freely without us overwriting their choice.
  useEffect(() => {
    if (catalogLabourRate <= 0) return;
    let mutated = false;
    const next = services.map((s) => ({
      ...s,
      components: s.components.map((c) => {
        if (c.kind !== 'labour') return c;
        if ((c.unitPriceExGst ?? 0) > 0) return c;
        mutated = true;
        const u = effectiveUnits(c);
        const total = round2(u * catalogLabourRate);
        return {
          ...c,
          unitPriceExGst: catalogLabourRate,
          buyPriceExGst: total,
          // Labour sells = cost — markup is implicit in the rate.
          // Only seed sell if it was unset (don't clobber a tech's
          // earlier override).
          sellPrice: (c.sellPrice ?? 0) > 0 ? c.sellPrice : total,
        };
      }),
    }));
    if (mutated) onChange(next);
    // services & onChange are intentionally omitted — including services
    // would cause every parent re-render to re-run this, and the body is
    // already a no-op once all labour rows have rate > 0.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogLabourRate]);

  const updateService = (
    serviceId: string,
    patch: Partial<RepairService>,
  ): void => {
    onChange(
      services.map((s) => (s.id === serviceId ? { ...s, ...patch } : s)),
    );
  };

  const updateComponent = (
    serviceId: string,
    componentId: string,
    patch: Partial<RepairServiceComponent>,
  ): void => {
    onChange(
      services.map((s) => {
        if (s.id !== serviceId) return s;
        return {
          ...s,
          components: s.components.map((c) =>
            c.id === componentId ? { ...c, ...patch } : c,
          ),
        };
      }),
    );
  };

  const removeService = (serviceId: string): void => {
    onChange(
      services
        .filter((s) => s.id !== serviceId)
        .map((s, idx) => ({ ...s, position: idx })),
    );
  };

  const removeComponent = (serviceId: string, componentId: string): void => {
    onChange(
      services.map((s) =>
        s.id === serviceId
          ? {
              ...s,
              components: s.components.filter((c) => c.id !== componentId),
            }
          : s,
      ),
    );
  };

  const seedComponent = (
    kind: RepairServiceComponent['kind'],
    options?: {
      componentCategories?: string[];
      units?: number;
      productCode?: string | null;
      description?: string;
      vendorId?: string | null;
      vendorName?: string | null;
      markupType?: 'fixed' | null;
      markupValue?: number;
      vendorQuoteStatus?: RepairServiceComponent['vendorQuoteStatus'];
      leadTime?: string | null;
    },
  ): RepairServiceComponent => {
    if (kind === 'third_party') {
      return {
        id: newId(),
        kind: 'third_party',
        description: options?.description ?? '',
        productCode: options?.productCode ?? null,
        units: 1,
        unitPriceExGst: 0,
        buyPriceExGst: 0,
        shipping: 0,
        sellPrice: 0,
        url: null,
        componentType: null,
        vendorId: options?.vendorId ?? null,
        vendorName: options?.vendorName ?? null,
        // The first line seeded by `addService` defaults to `accepted` so
        // the single-vendor workflow gives an immediate quote total.
        // Additional lines added via "Add another vendor quote" pass
        // `awaiting` explicitly — those represent in-flight quote
        // comparisons that shouldn't count toward the total.
        vendorQuoteStatus: options?.vendorQuoteStatus ?? 'accepted',
        markupType: options?.markupType ?? null,
        markupValue: options?.markupValue ?? 0,
        leadTime: options?.leadTime ?? null,
      };
    }
    if (kind === 'fixed_price') {
      // Flat-rate service line. The tech types (or pastes) a product
      // code and a sell price; we don't carry a units/rate/markup
      // breakdown the way labour or third-party do. The catalog price
      // is loaded via the same getProductBySku lookup once the tech
      // edits the productCode field — we seed at 0 here so a freshly-
      // added row reads as "needs price" rather than misrepresenting
      // an arbitrary catalog default.
      return {
        id: newId(),
        kind: 'fixed_price',
        description: options?.description ?? '',
        productCode: options?.productCode ?? null,
        units: 1,
        unitPriceExGst: 0,
        buyPriceExGst: 0,
        shipping: 0,
        sellPrice: 0,
        url: null,
        componentType: null,
      };
    }
    if (kind === 'labour') {
      // Prefer the live, reactive `catalogLabourRate` (sourced from
      // useProductCatalog().products) over the synchronous
      // `labourRateForSku` lookup — at the moment of seeding, the latter
      // can race the catalog fetch on a fresh page load and seed 0.
      const rate = catalogLabourRate > 0
        ? catalogLabourRate
        : labourRateForSku(RCS_REPAIR_LABOUR_SKU);
      const units =
        Number.isFinite(options?.units) && (options?.units ?? 0) > 0
          ? (options!.units as number)
          : 1;
      const total = round2(units * rate);
      return {
        id: newId(),
        kind: 'labour',
        description: LABOUR_DEFAULT_DESCRIPTION,
        productCode: RCS_REPAIR_LABOUR_SKU,
        units,
        unitPriceExGst: rate,
        buyPriceExGst: total,
        shipping: 0,
        sellPrice: total, // seed sell to match cost; tech adjusts
        url: null,
        componentType: null,
      };
    }
    // Pre-set the part's componentType to the parent service's first
    // listed category so the mismatch-warning logic has a sensible
    // starting tag. Empty / missing keeps componentType null.
    const presetCategory =
      options?.componentCategories?.find((c) => c && c.trim().length > 0) ??
      null;
    return {
      id: newId(),
      kind: 'part',
      description: '',
      productCode: HW_REPAIR_PART_SKU,
      units: 1,
      unitPriceExGst: 0,
      buyPriceExGst: 0,
      shipping: 0,
      sellPrice: 0, // tracks cost once the part cost is entered (no markup)
      url: null,
      componentType: presetCategory,
      // Default to ex-GST — distis are our most common supplier and
      // quote ex-GST. The tech can flip to 'inc' per line for retail
      // sources without affecting the quote's customer GST mode.
      supplierGstMode: 'ex',
      // Most parts on a repair quote need to be ordered from a
      // supplier; the tech flips to 'stock' when the shop already has
      // the part on hand. Drives the "Send to ordering" eligibility.
      fulfillmentSource: 'order',
    };
  };

  const addService = (picked: RepairServiceType): void => {
    const id = newId();
    const categories = picked.componentCategories ?? [];
    // Admin-controlled flag: explicit true/false on the row means
    // "auto-add a part". When the column isn't set (legacy rows pre-
    // dating the flag), fall back to the original behaviour of
    // inferring it from componentCategories.
    const requiresPart =
      picked.requiresPart ?? categories.length > 0;
    // Admin-controlled default for the labour Hours seed. Falls back
    // to 1 hour when unset.
    const defaultUnits =
      Number.isFinite(picked.defaultLabourUnits) &&
      (picked.defaultLabourUnits ?? 0) > 0
        ? (picked.defaultLabourUnits as number)
        : 1;

    // Third-party services swap the usual labour [+ part] seed for one
    // third-party cost line per admin-configured vendor. Vendor +
    // markup config is snapshotted onto each component so the lines
    // remain stable if the admin later edits the RepairServiceType.
    //
    //   - 1 vendor configured  → seed a single line, status 'accepted'
    //                             (the simple workflow — no quote
    //                             comparison needed).
    //   - 2+ vendors configured → seed N lines, status 'awaiting' on
    //                             each. The tech updates statuses and
    //                             marks one 'accepted' once they pick
    //                             a vendor.
    //   - 0 vendors configured (legacy / mis-config) → seed a single
    //                             blank line so the row is editable.
    let components: RepairServiceComponent[];
    if (picked.isFixedPriceService) {
      // Flat-rate service — seed a single fixed_price component driven by
      // the picked catalog product. Sell price is the catalog `unitCost`
      // (stored inc-GST) converted to ex-GST; tech can override per quote
      // via the Sell field. No labour/parts breakdown is needed.
      const product = getProductBySku(picked.repairServiceCode);
      const seeded = seedComponent('fixed_price', {
        productCode: picked.repairServiceCode,
        description: picked.displayName || '',
      });
      seeded.sellPrice = catalogIncToExGst(product?.unitCost);
      components = [seeded];
    } else if (picked.isThirdParty) {
      const vendorIds = picked.thirdPartyVendorIds ?? [];
      const lookupName = (id: string): string | null => {
        const v = vendors.find((x) => x.vendorId === id);
        return v ? v.name : null;
      };
      const baseSeedOptions = {
        productCode: picked.repairServiceCode,
        description: picked.displayName || '',
        markupType: picked.markupType ?? null,
        markupValue: picked.markupValue ?? 0,
      };
      if (vendorIds.length === 0) {
        components = [
          seedComponent('third_party', {
            ...baseSeedOptions,
            vendorId: null,
            vendorName: null,
            vendorQuoteStatus: 'accepted',
          }),
        ];
      } else if (vendorIds.length === 1) {
        components = [
          seedComponent('third_party', {
            ...baseSeedOptions,
            vendorId: vendorIds[0],
            vendorName: lookupName(vendorIds[0]),
            vendorQuoteStatus: 'accepted',
          }),
        ];
      } else {
        components = vendorIds.map((vid) =>
          seedComponent('third_party', {
            ...baseSeedOptions,
            vendorId: vid,
            vendorName: lookupName(vid),
            vendorQuoteStatus: 'awaiting',
          }),
        );
      }
    } else {
      components = [
        seedComponent('labour', { units: defaultUnits }),
        ...(requiresPart
          ? [seedComponent('part', { componentCategories: categories })]
          : []),
      ];
    }

    // If a part line was seeded, queue its Source field to autofocus on
    // the next render — that's the first decision the tech makes
    // (Order vs In stock) and saves a click.
    const newPart = components.find((c) => c.kind === 'part');
    if (newPart) setPendingFocusSourceId(newPart.id);

    // For fixed-price services the single component description is what
    // the customer sees on the invoice, so seed svc.description from it
    // too — the editor hides the separate service-level field in this
    // mode and edits sync back to keep them aligned.
    const initialDescription = picked.isFixedPriceService
      ? components[0]?.description || picked.displayName || null
      : null;

    onChange([
      ...services,
      {
        id,
        position: services.length,
        repairServiceCode: picked.repairServiceCode,
        repairServiceName: picked.displayName || picked.repairServiceCode,
        description: initialDescription,
        components,
      },
    ]);

    // Surface this service's default terms template to the parent so it can
    // pre-fill the quote's terms template (first-service-wins, fill-if-empty
    // is enforced by the parent, which owns that state).
    if (picked.defaultQuoteTermsTemplateId && onServiceDefaultTermsTemplate) {
      onServiceDefaultTermsTemplate(picked.defaultQuoteTermsTemplateId);
    }
  };

  // Auto-seed the locked service (network installs) so the tech lands on a
  // ready-to-edit "Network Installation" line instead of an empty picker.
  // Runs once: once a service exists (seeded here or loaded from the server)
  // the ref latches and we never re-seed — important because the editor
  // hides the Remove button in locked mode, so the list can't drop back to
  // empty during a session.
  const autoSeededRef = useRef(false);
  useEffect(() => {
    if (!lockedServiceCode || autoSeededRef.current) return;
    // Never inject state into a read-only / promoted quote.
    if (disabled) return;
    if (services.length > 0) {
      autoSeededRef.current = true;
      return;
    }
    // Wait for the catalog so we can seed from the admin-managed row
    // (display name, default labour units, requiresPart, etc.).
    if (serviceTypesLoading) return;
    autoSeededRef.current = true;
    const picked = serviceTypes.find(
      (t) => t.repairServiceCode === lockedServiceCode,
    );
    if (picked) {
      addService(picked);
    } else {
      // Catalog has no matching row — seed a minimal service so the tech can
      // still log labour/parts against the locked line.
      onChange([
        {
          id: newId(),
          position: 0,
          repairServiceCode: lockedServiceCode,
          repairServiceName: lockedServiceName || lockedServiceCode,
          description: null,
          components: [seedComponent('labour', {})],
        },
      ]);
    }
    // addService/seedComponent/onChange are stable enough for a once-only
    // seed guarded by autoSeededRef; re-running on their identity would risk
    // a double seed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedServiceCode, serviceTypesLoading, services.length]);

  const addComponent = (
    serviceId: string,
    kind: RepairServiceComponent['kind'],
  ): void => {
    onChange(
      services.map((s) => {
        if (s.id !== serviceId) return s;
        // Fixed-price lines are driven by a catalog product. The service
        // itself was picked from the same catalog (via RepairServicePicker),
        // so seed the new line's productCode from the service's
        // repairServiceCode and pull the catalog's unitCost (inc-GST) as the
        // ex-GST sell. Tech can edit the productCode or sell freely after.
        let seeded: RepairServiceComponent;
        if (kind === 'fixed_price' && s.repairServiceCode) {
          const product = getProductBySku(s.repairServiceCode);
          seeded = seedComponent('fixed_price', {
            productCode: s.repairServiceCode,
          });
          seeded.sellPrice = catalogIncToExGst(product?.unitCost);
        } else {
          seeded = seedComponent(kind);
        }
        return { ...s, components: [...s.components, seeded] };
      }),
    );
  };

  /** Append a part component seeded from a picked catalog product. The
   *  catalog `unitCost` is inc-GST, so it's converted to our canonical
   *  ex-GST rate; parts sell at cost (no markup — the margin lives in the
   *  labour rate), so sell tracks the cost. Sourced from our own catalog,
   *  so it defaults to 'stock' rather than the blank-part 'order' default;
   *  the tech flips Source to 'order' if it needs a PO. */
  const addPartFromInventory = (
    serviceId: string,
    product: ProductsListItem,
  ): void => {
    // Keep full precision on the inc→ex divide — do NOT round2 here. A
    // stock part displays its sell inc-GST, so the stored ex value is
    // multiplied back by 1.1 at render time; pre-rounding (e.g. 115 inc →
    // 104.55 ex → 115.005 → $115.01) leaks a cent into the quoted amount.
    // Display sites round via round2, so the round-trip lands on $115.00.
    const exCost = catalogIncToExGst(product.unitCost); // units = 1, no shipping
    onChange(
      services.map((s) => {
        if (s.id !== serviceId) return s;
        const seeded = seedComponent('part');
        const part: RepairServiceComponent = {
          ...seeded,
          description: product.displayName || product.sku || '',
          productCode: product.sku || seeded.productCode,
          units: 1,
          unitPriceExGst: exCost,
          buyPriceExGst: exCost,
          sellPrice: exCost,
          fulfillmentSource: 'stock',
        };
        return { ...s, components: [...s.components, part] };
      }),
    );
  };

  /** Append a standalone part — a plain product line sold alongside the
   *  repair (e.g. a 1TB HDD on a data-recovery job), not tied to any repair
   *  service. Modelled as a `RepairService` flagged `isStandalonePart` with a
   *  single `fixed_price` component holding the sell price (seeded at cost —
   *  the tech overrides), so it reuses the existing storage and promotes to
   *  its own invoice line (name = product SKU, price = the component sell). */
  const addStandalonePart = (product: ProductsListItem): void => {
    // Keep full precision on the inc→ex divide (see addPartFromInventory).
    const exCost = catalogIncToExGst(product.unitCost);
    const label = product.displayName || product.sku || '';
    const comp = seedComponent('fixed_price', {
      productCode: product.sku || null,
      description: label,
    });
    comp.sellPrice = exCost;
    comp.buyPriceExGst = exCost;
    onChange([
      ...services,
      {
        id: newId(),
        position: services.length,
        repairServiceCode: product.sku || HW_REPAIR_PART_SKU,
        repairServiceName: label,
        description: label || null,
        components: [comp],
        isStandalonePart: true,
      },
    ]);
  };

  /** Append a fresh third-party vendor-quote line to a service that
   *  already has one. Snapshots the markup config off the existing
   *  third-party line so all quote-comparison rows on the service share
   *  the same uplift. New lines default to status `awaiting`. */
  const addThirdPartyQuoteLine = (serviceId: string): void => {
    onChange(
      services.map((s) => {
        if (s.id !== serviceId) return s;
        const existing = s.components.find((c) => c.kind === 'third_party');
        return {
          ...s,
          components: [
            ...s.components,
            seedComponent('third_party', {
              productCode: existing?.productCode ?? null,
              description: existing?.description ?? '',
              vendorId: null,
              vendorName: null,
              markupType: existing?.markupType ?? null,
              markupValue: existing?.markupValue ?? 0,
              vendorQuoteStatus: 'awaiting',
            }),
          ],
        };
      }),
    );
  };

  /** Patch a component while enforcing the single-accepted rule on
   *  third-party rows: when a row is marked `accepted`, any sibling
   *  third-party row that was previously `accepted` drops to
   *  `received` (preserving the cost snapshot for audit). */
  const updateComponentEnforced = (
    serviceId: string,
    componentId: string,
    patch: Partial<RepairServiceComponent>,
  ): void => {
    onChange(
      services.map((s) => {
        if (s.id !== serviceId) return s;
        const becomingAccepted =
          patch.vendorQuoteStatus === 'accepted';
        return {
          ...s,
          components: s.components.map((c) => {
            if (c.id === componentId) {
              return { ...c, ...patch };
            }
            // Demote other accepted third-party siblings.
            if (
              becomingAccepted &&
              c.kind === 'third_party' &&
              c.vendorQuoteStatus === 'accepted'
            ) {
              return { ...c, vendorQuoteStatus: 'received' };
            }
            return c;
          }),
        };
      }),
    );
  };

  /** Edit a numeric field on a component and keep `buyPriceExGst` in sync
   *  with `units × unitPriceExGst + shipping`. The tech can still override
   *  `buyPriceExGst` directly via the Cost field — that path bypasses
   *  this helper. */
  const updateComponentNumeric = (
    serviceId: string,
    componentId: string,
    patch: Partial<
      Pick<
        RepairServiceComponent,
        'units' | 'unitPriceExGst' | 'shipping'
      >
    >,
  ): void => {
    onChange(
      services.map((s) => {
        if (s.id !== serviceId) return s;
        return {
          ...s,
          components: s.components.map((c) => {
            if (c.id !== componentId) return c;
            const next = { ...c, ...patch };
            next.buyPriceExGst = computeBuyTotal(next);
            if (next.kind === 'labour') {
              // Labour: sell tracks cost 1:1. Rate is the customer-facing
              // hourly rate, so there's no separate markup added on top.
              next.sellPrice = next.buyPriceExGst;
            } else if (next.kind === 'third_party') {
              // Third-party: sell = cost + (markup% or markup$). Uses
              // the snapshotted markup stored on the component.
              next.sellPrice = computeThirdPartySell(next);
            } else {
              // Parts: sell tracks cost 1:1 — no markup on parts for a
              // repair service (the margin lives in the labour rate).
              // The tech can override the Sell field afterwards.
              next.sellPrice = round2(next.buyPriceExGst);
            }
            return next;
          }),
        };
      }),
    );
  };

  const grandTotal = useMemo(
    () => services.reduce((acc, s) => acc + serviceTotal(s, gstInclusive), 0),
    [services, gstInclusive],
  );

  const grandProfit = useMemo(() => {
    let cost = 0;
    let sell = 0;
    let low = 0;
    let hasData = false;
    for (const s of services) {
      const p = serviceProfit(s);
      if (p.hasData) hasData = true;
      cost += p.costExGst;
      sell += p.sellExGst;
      low += p.lowCount;
    }
    return {
      hasData,
      profit: sell - cost,
      margin: sell > 0 ? (sell - cost) / sell : null,
      lowCount: low,
    };
  }, [services]);

  /** Expanded cost/sourcing fields for an order-part line. Stored values
   *  stay ex-GST; the per-line supplier GST mode only flips the displayed
   *  inputs. Mirrors the precision-preserving inc→ex rules used elsewhere. */
  const renderPartSourcing = (
    svc: RepairService,
    comp: RepairServiceComponent,
  ): React.ReactNode => {
    const unitPriceValue = effectiveUnitPrice(comp);
    const leadTimeValue = comp.leadTime ?? '';
    const leadTimeValid = isLeadTimeValid(leadTimeValue);
    const partGstInc = comp.supplierGstMode === 'inc';
    const displayUnit = partGstInc ? unitPriceValue * 1.1 : unitPriceValue;
    const displayShipping = partGstInc
      ? comp.shipping * 1.1
      : comp.shipping;
    const displayCost = partGstInc
      ? comp.buyPriceExGst * 1.1
      : comp.buyPriceExGst;
    const partVendorOpts = [...partVendorPool];
    if (
      comp.vendorId &&
      !partVendorOpts.some((v) => v.vendorId === comp.vendorId)
    ) {
      partVendorOpts.unshift({
        vendorId: comp.vendorId,
        code: '',
        name: comp.vendorName ?? '(unknown vendor)',
        isActive: false,
        isDisti: false,
      } as Vendor);
    }
    const currentPartVendor =
      partVendorOpts.find((v) => v.vendorId === comp.vendorId) ?? null;
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'flex-end',
        }}
      >
        <Autocomplete
          size="small"
          options={partVendorOpts}
          value={currentPartVendor}
          onChange={(_, v) => {
            if (v) {
              updateComponent(svc.id, comp.id, {
                vendorId: v.vendorId,
                vendorName: v.name,
                supplierGstMode: v.isRetailer ? 'inc' : 'ex',
              });
            } else {
              updateComponent(svc.id, comp.id, {
                vendorId: null,
                vendorName: null,
              });
            }
          }}
          getOptionLabel={(o) => (o ? o.name : '')}
          isOptionEqualToValue={(o, v) => o.vendorId === v.vendorId}
          disabled={disabled}
          sx={{ width: 200 }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label="Vendor"
              placeholder="Select…"
            />
          )}
        />
        <TextField
          select
          variant="standard"
          size="small"
          label="Supplier GST"
          value={comp.supplierGstMode ?? 'ex'}
          onChange={(e) =>
            updateComponent(svc.id, comp.id, {
              supplierGstMode: e.target.value as 'ex' | 'inc',
            })
          }
          disabled={disabled}
          sx={{ width: 110 }}
        >
          <MenuItem value="ex">Ex-GST</MenuItem>
          <MenuItem value="inc">Inc-GST</MenuItem>
        </TextField>
        <TextField
          variant="standard"
          size="small"
          label={partGstInc ? 'Unit cost inc-GST' : 'Unit cost ex-GST'}
          type="number"
          value={round2(displayUnit)}
          onChange={(e) => {
            const raw = Number(e.target.value) || 0;
            const ex = partGstInc ? raw / 1.1 : round2(raw);
            updateComponentNumeric(svc.id, comp.id, { unitPriceExGst: ex });
          }}
          disabled={disabled}
          sx={{ width: 140 }}
          inputProps={{ step: '0.01', min: 0 }}
        />
        <TextField
          variant="standard"
          size="small"
          label={partGstInc ? 'Shipping inc-GST' : 'Shipping ex-GST'}
          type="number"
          value={round2(displayShipping)}
          onChange={(e) => {
            const raw = Number(e.target.value) || 0;
            const ex = partGstInc ? raw / 1.1 : round2(raw);
            updateComponentNumeric(svc.id, comp.id, { shipping: ex });
          }}
          disabled={disabled}
          sx={{ width: 130 }}
          inputProps={{ step: '0.01', min: 0 }}
        />
        <Tooltip title="Auto = qty × unit cost + shipping. Editable to override.">
          <TextField
            variant="standard"
            size="small"
            label={partGstInc ? 'Cost inc-GST' : 'Cost ex-GST'}
            type="number"
            value={round2(displayCost)}
            onChange={(e) => {
              const raw = Number(e.target.value) || 0;
              const ex = partGstInc ? raw / 1.1 : round2(raw);
              updateComponent(svc.id, comp.id, { buyPriceExGst: ex });
            }}
            disabled={disabled}
            sx={{ width: 130 }}
            inputProps={{ step: '0.01' }}
          />
        </Tooltip>
        <Autocomplete
          size="small"
          freeSolo
          options={LEAD_TIME_SUGGESTIONS}
          value={leadTimeValue}
          onChange={(_, v) =>
            updateComponent(svc.id, comp.id, {
              leadTime: (v || null) as string | null,
            })
          }
          onInputChange={(_, v, reason) => {
            if (reason === 'input') {
              updateComponent(svc.id, comp.id, { leadTime: v || null });
            }
          }}
          disabled={disabled}
          sx={{ width: 150 }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label="Lead time"
              placeholder="e.g. 1-2 weeks"
              error={!leadTimeValid}
              helperText={
                leadTimeValid ? undefined : 'Use e.g. "2-3 days" or "1 week"'
              }
            />
          )}
        />
        <TextField
          variant="standard"
          size="small"
          label="Supplier URL"
          value={comp.url ?? ''}
          onChange={(e) =>
            updateComponent(svc.id, comp.id, { url: e.target.value || null })
          }
          onBlur={(e) => {
            const cleaned = cleanSupplierUrl(e.target.value);
            if (cleaned !== e.target.value) {
              updateComponent(svc.id, comp.id, { url: cleaned || null });
            }
          }}
          placeholder="https://…"
          disabled={disabled}
          sx={{ flex: 1, minWidth: 220 }}
        />
      </Box>
    );
  };

  /** Expanded vendor-quote fields for a third-party line: vendor, status,
   *  cost, lead time, and the snapshotted markup that drives the sell. */
  const renderThirdPartyDetail = (
    svc: RepairService,
    comp: RepairServiceComponent,
    leadTimeValue: string,
    leadTimeValid: boolean,
  ): React.ReactNode => {
    const unitPriceValue = effectiveUnitPrice(comp);
    const opts = [...thirdPartyVendorPool];
    if (comp.vendorId && !opts.some((v) => v.vendorId === comp.vendorId)) {
      opts.unshift({
        vendorId: comp.vendorId,
        code: '',
        name: comp.vendorName ?? '(unknown vendor)',
        isActive: false,
        isDisti: false,
      } as Vendor);
    }
    const current = opts.find((v) => v.vendorId === comp.vendorId) ?? null;
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'flex-end',
        }}
      >
        <Autocomplete
          size="small"
          options={opts}
          value={current}
          onChange={(_, v) =>
            updateComponent(svc.id, comp.id, {
              vendorId: v?.vendorId ?? null,
              vendorName: v?.name ?? null,
            })
          }
          getOptionLabel={(o) => (o ? o.name : '')}
          isOptionEqualToValue={(o, v) => o.vendorId === v.vendorId}
          disabled={disabled}
          sx={{ width: 200 }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label="Vendor"
              placeholder="Select…"
            />
          )}
        />
        <TextField
          select
          variant="standard"
          size="small"
          label="Status"
          value={comp.vendorQuoteStatus ?? 'awaiting'}
          onChange={(e) =>
            updateComponentEnforced(svc.id, comp.id, {
              vendorQuoteStatus: e.target
                .value as RepairServiceComponent['vendorQuoteStatus'],
            })
          }
          disabled={disabled}
          sx={{ width: 160 }}
        >
          {VENDOR_QUOTE_STATUS_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          variant="standard"
          size="small"
          label={gstInclusive ? 'Cost inc-GST' : 'Cost ex-GST'}
          type="number"
          value={round2(gstInclusive ? unitPriceValue * 1.1 : unitPriceValue)}
          onChange={(e) => {
            const raw = Number(e.target.value) || 0;
            const ex = gstInclusive ? raw / 1.1 : round2(raw);
            updateComponentNumeric(svc.id, comp.id, { unitPriceExGst: ex });
          }}
          disabled={disabled}
          sx={{ width: 140 }}
          inputProps={{ step: '0.01', min: 0 }}
        />
        <Autocomplete
          size="small"
          freeSolo
          options={LEAD_TIME_SUGGESTIONS}
          value={leadTimeValue}
          onChange={(_, v) =>
            updateComponent(svc.id, comp.id, {
              leadTime: (v || null) as string | null,
            })
          }
          onInputChange={(_, v, reason) => {
            if (reason === 'input') {
              updateComponent(svc.id, comp.id, { leadTime: v || null });
            }
          }}
          disabled={disabled}
          sx={{ width: 150 }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label="Lead time"
              placeholder="e.g. 1-2 weeks"
              error={!leadTimeValid}
              helperText={
                leadTimeValid ? undefined : 'Use e.g. "2-3 days" or "1 week"'
              }
            />
          )}
        />
        <TextField
          variant="standard"
          size="small"
          label={gstInclusive ? 'Markup inc-GST' : 'Markup ex-GST'}
          type="number"
          value={round2(
            gstInclusive
              ? (comp.markupValue ?? 0) * 1.1
              : (comp.markupValue ?? 0),
          )}
          onChange={(e) => {
            const raw = Number(e.target.value) || 0;
            const ex = gstInclusive ? raw / 1.1 : round2(raw);
            const cost = Number.isFinite(comp.buyPriceExGst)
              ? comp.buyPriceExGst
              : 0;
            updateComponent(svc.id, comp.id, {
              markupType: 'fixed',
              markupValue: ex,
              sellPrice: round2(cost + ex),
            });
          }}
          disabled={disabled}
          sx={{ width: 140 }}
          inputProps={{ step: '0.01', min: 0 }}
        />
      </Box>
    );
  };

  return (
    <Stack spacing={2}>
      {services.length === 0 && (
        <Alert severity="info">
          Add a repair service to start quoting. Each service rolls up
          internal labour + parts into one customer-facing line.
        </Alert>
      )}

      {services.map((svc, idx) => {
        // Standalone parts reuse the RepairService shape but are plain
        // product lines, not repair services — render a compact card
        // (description + sell + remove) instead of the full service editor.
        if (svc.isStandalonePart) {
          const comp = svc.components[0];
          return (
            <Paper
              key={svc.id}
              variant="outlined"
              sx={{ p: 2, bgcolor: 'background.default' }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, minWidth: 24 }}
                >
                  #{idx + 1}
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Chip
                      size="small"
                      icon={<PartIcon sx={{ fontSize: '1rem' }} />}
                      label="Standalone part"
                      variant="outlined"
                      color="secondary"
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={svc.repairServiceCode || '— no code —'}
                      color={svc.repairServiceCode ? 'primary' : 'default'}
                    />
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {fmtCurrency(withEft(serviceTotal(svc, gstInclusive)))}
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 0.5 }}
                      >
                        {gstInclusive ? 'inc GST' : 'ex GST'}
                      </Typography>
                    </Typography>
                    <Tooltip title="Remove this part">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => removeService(svc.id)}
                          disabled={disabled}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="flex-end">
                    <TextField
                      size="small"
                      fullWidth
                      label="Description (shown to customer)"
                      value={svc.description ?? ''}
                      onChange={(e) => {
                        // Keep the service name + component description in
                        // sync with the customer-facing description in one
                        // atomic update (two onChange calls would clobber).
                        const next = e.target.value;
                        onChange(
                          services.map((s) =>
                            s.id === svc.id
                              ? {
                                  ...s,
                                  description: next || null,
                                  repairServiceName: next,
                                  components: s.components.map((c, i) =>
                                    i === 0 ? { ...c, description: next } : c,
                                  ),
                                }
                              : s,
                          ),
                        );
                      }}
                      disabled={disabled}
                    />
                    <Box sx={{ width: 150, flexShrink: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', textAlign: 'right' }}
                      >
                        Sell {gstInclusive ? '(inc GST)' : '(ex GST)'}
                      </Typography>
                      {comp && (
                        <SellPriceInput
                          label={gstInclusive ? 'Sell inc-GST' : 'Sell ex-GST'}
                          valueExGst={comp.sellPrice}
                          gstInclusive={gstInclusive}
                          disabled={disabled}
                          onChange={(nextExGst) =>
                            updateComponent(svc.id, comp.id, {
                              sellPrice: nextExGst,
                              buyPriceExGst: nextExGst,
                            })
                          }
                        />
                      )}
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          );
        }
        // A service is "fixed-price-only" when every component on it is a
        // flat-rate fixed_price line — i.e. there's no internal labour /
        // part breakdown to justify the price. We hide the "Internal
        // components" framing in that case, since for these services the
        // single line IS the customer-facing price (driven by the picked
        // catalog product, overridable via the Sell field).
        const isFixedPriceOnly =
          svc.components.length > 0 &&
          svc.components.every((c) => c.kind === 'fixed_price');
        // Shared add-component buttons. Rendered once above the component
        // list and again below it (for services with components) so the
        // tech doesn't have to scroll back up to the top of a long list to
        // add another labour/part/fixed-price line.
        const addComponentButtons = (
          <>
            {svc.components.some((c) => c.kind === 'third_party') && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => addThirdPartyQuoteLine(svc.id)}
                disabled={disabled}
              >
                Add another vendor quote
              </Button>
            )}
            <Button
              size="small"
              startIcon={<BuildIcon />}
              onClick={() => addComponent(svc.id, 'labour')}
              disabled={disabled}
            >
              Add labour
            </Button>
            <Button
              size="small"
              startIcon={<PartIcon />}
              onClick={() => addComponent(svc.id, 'part')}
              disabled={disabled}
            >
              Add orderable part
            </Button>
            <Button
              size="small"
              startIcon={<SearchIcon />}
              onClick={() => setPartPickerServiceId(svc.id)}
              disabled={disabled}
            >
              Add part from inventory
            </Button>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => addComponent(svc.id, 'fixed_price')}
              disabled={disabled}
            >
              Add fixed price
            </Button>
          </>
        );
        return (
        <Paper
          key={svc.id}
          variant="outlined"
          sx={{ p: 2, bgcolor: 'background.default' }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-start"
            sx={{ mb: 1 }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, minWidth: 24 }}
            >
              #{idx + 1}
            </Typography>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                {lockedServiceCode ? (
                  // Locked mode (network installs): the service is fixed, so
                  // show its name as a static label instead of the device-
                  // scoped picker.
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {svc.repairServiceName ||
                      lockedServiceName ||
                      'Network Installation'}
                  </Typography>
                ) : (
                  <RepairServicePicker
                    value={svc.repairServiceCode}
                    deviceType={deviceType}
                    disabled={disabled}
                    onPick={(picked) =>
                      updateService(svc.id, {
                        repairServiceCode: picked.repairServiceCode,
                        repairServiceName:
                          picked.displayName || picked.repairServiceCode,
                      })
                    }
                  />
                )}
                {(() => {
                  // Surface a parts-split chip so a manager scanning the
                  // quote sees how many parts are coming from stock vs
                  // needing a PO without expanding each component.
                  const parts = (svc.components || []).filter(
                    (c) => c.kind === 'part',
                  );
                  if (parts.length === 0) return null;
                  const stock = parts.filter(
                    (c) => c.fulfillmentSource === 'stock',
                  ).length;
                  const order = parts.length - stock;
                  return (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${order} to order · ${stock} in stock`}
                      color={order > 0 ? 'warning' : 'success'}
                    />
                  );
                })()}
                <Box sx={{ flex: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {fmtCurrency(serviceTotal(svc, gstInclusive))}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 0.5 }}
                  >
                    {gstInclusive ? 'inc GST' : 'ex GST'}
                  </Typography>
                </Typography>
                {/* The locked service can't be removed — it's the single
                    fixed line the quote bills against. */}
                {!lockedServiceCode && (
                  <Tooltip title="Remove this repair service">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => removeService(svc.id)}
                        disabled={disabled}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>

              {/* Fixed-price-only services use the single component's
                  description as the customer-facing line description —
                  there's no labour/parts breakdown to consolidate, so the
                  service-level field would just be a duplicate. The
                  component description below is synced back to
                  svc.description on edit. */}
              {!isFixedPriceOnly && (
                <TextField
                  size="small"
                  multiline
                  minRows={1}
                  fullWidth
                  label="Customer-facing description (per service)"
                  value={svc.description ?? ''}
                  onChange={(e) =>
                    updateService(svc.id, {
                      description: e.target.value || null,
                    })
                  }
                  disabled={disabled}
                  helperText="Optional. Empty falls back to the quote's consolidated description."
                  sx={{ mt: 1.5 }}
                />
              )}

              {/* Customer-facing preview note — mutually exclusive options.
                  "Includes all parts and labour" is the default blanket note;
                  "Include part details" lists this service's parts instead.
                  Appends a line under the service in the quote preview AND on
                  the service's invoice line (`repairServiceBlurb`). Hidden
                  for flat-rate (fixed-price) services that carry no
                  labour/parts breakdown. */}
              {!isFixedPriceOnly && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Quote and Invoice Preview Options
                  </Typography>
                  <RadioGroup
                    sx={{ pl: 0.5 }}
                    // Defaults to the blanket note when neither flag is set.
                    value={svc.includePartDetails ? 'parts' : 'all'}
                    onChange={(e) =>
                      updateService(svc.id, {
                        includePartDetails: e.target.value === 'parts',
                        includeAllPartsLabour: e.target.value === 'all',
                      })
                    }
                  >
                    <FormControlLabel
                      value="parts"
                      // Itemised part details only make sense when the service
                      // actually has parts — offered but disabled otherwise.
                      control={
                        <Radio
                          size="small"
                          disabled={
                            disabled ||
                            !svc.components.some((c) => c.kind === 'part')
                          }
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Include part details
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      value="all"
                      control={<Radio size="small" disabled={disabled} />}
                      label={
                        <Typography variant="body2">
                          Show &quot;Includes all parts and labour&quot;
                        </Typography>
                      }
                    />
                  </RadioGroup>
                  {/* Editable closing bullet for the itemised "Includes:"
                      block (e.g. "installation and post install testing").
                      Only relevant when the parts list is shown. Clearing it
                      removes the closing line from the preview. */}
                  {svc.includePartDetails && (
                    <TextField
                      size="small"
                      fullWidth
                      label="Includes: closing line"
                      value={
                        svc.includesClosingLine ??
                        DEFAULT_INCLUDES_CLOSING_LINE
                      }
                      onChange={(e) =>
                        updateService(svc.id, {
                          includesClosingLine: e.target.value,
                        })
                      }
                      disabled={disabled}
                      helperText="Closes the itemised list. Clear to omit this line."
                      sx={{ mt: 1, pl: 0.5 }}
                    />
                  )}
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />

              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {!isFixedPriceOnly && (
                    <Typography variant="caption" color="text.secondary">
                      Internal components (not shown to customer)
                    </Typography>
                  )}
                  <Box sx={{ flex: 1 }} />
                  {/* The "Add another vendor quote" affordance only
                      appears for third-party services (i.e. services
                      that already have at least one third_party
                      component). Labour / part buttons stay available
                      so the tech can still log time / parts attached
                      to a sub-contracted repair if needed. */}
                  {addComponentButtons}
                </Stack>

                {svc.components.length === 0 && (
                  <Alert severity="warning" variant="outlined">
                    Add a labour or fixed-price component before this
                    service can be promoted to an invoice.
                  </Alert>
                )}

                {svc.components.length > 0 && (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 760 }}>
                      {/* Column header — labels shown once for the whole
                          service instead of repeated on every field. */}
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: GRID_TEMPLATE,
                          columnGap: 1,
                          alignItems: 'center',
                          px: 1,
                          py: 0.5,
                          borderBottom: '2px solid',
                          borderColor: 'divider',
                          '& > *': {
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: 'text.secondary',
                          },
                        }}
                      >
                        <Box />
                        <Box>Kind</Box>
                        <Box>Item</Box>
                        <Box sx={{ textAlign: 'right' }}>Qty / Hrs</Box>
                        <Box>Rate / Source</Box>
                        <Box sx={{ textAlign: 'right' }}>
                          Sell {gstInclusive ? '(inc)' : '(ex)'}
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>GP</Box>
                        <Box />
                      </Box>

                      {svc.components.map((comp) => {
                        const unitsValue = effectiveUnits(comp);
                        const unitPriceValue = effectiveUnitPrice(comp);
                        const isLabour = comp.kind === 'labour';
                        const isThirdPartyComp = comp.kind === 'third_party';
                        const isFixedPriceComp = comp.kind === 'fixed_price';
                        const defaultCode = isLabour
                          ? RCS_REPAIR_LABOUR_SKU
                          : isThirdPartyComp
                            ? (comp.productCode ?? '')
                            : HW_REPAIR_PART_SKU;
                        const leadTimeValue = comp.leadTime ?? '';
                        const leadTimeValid = isLeadTimeValid(leadTimeValue);
                        const kindMeta = KIND_META[comp.kind];

                        // Profit / margin for the margin-bearing kinds. We
                        // still show the GP % figure, but no longer flag it:
                        // orderable parts on a repair quote are frequently
                        // ordered at low / no margin (cost recovery, customer-
                        // supplied, etc.), so the below-floor warning was noise.
                        const lineCost = computeBuyTotal(comp);
                        const verdict: MarginVerdict | null = hasMargin(comp)
                          ? evaluateMargin(lineCost, comp.sellPrice)
                          : null;
                        const showWarning = false;

                        // Row tint: accepted vendor line gets a green edge,
                        // declined / unable fade — same scan cue as before.
                        const rowDecorative = (() => {
                          if (!isThirdPartyComp) return {};
                          if (comp.vendorQuoteStatus === 'accepted') {
                            return {
                              borderLeft: '3px solid',
                              borderLeftColor: '#6dcb6d',
                            };
                          }
                          if (
                            comp.vendorQuoteStatus === 'declined' ||
                            comp.vendorQuoteStatus === 'unable_to_quote'
                          ) {
                            return { opacity: 0.55 };
                          }
                          return {};
                        })();

                        // Order-parts (sourcing) and third-party (vendor
                        // quote) carry a collapsible detail row. Stock parts
                        // have nothing to source; labour / fixed have none.
                        const hasDetailRow =
                          (comp.kind === 'part' &&
                            comp.fulfillmentSource !== 'stock') ||
                          isThirdPartyComp;
                        const detailOpen = expandedDetails.has(comp.id);

                        const sellGstInc =
                          ((isLabour || isThirdPartyComp || isFixedPriceComp) &&
                            gstInclusive) ||
                          (comp.kind === 'part' &&
                            (comp.fulfillmentSource === 'stock' ||
                              comp.supplierGstMode === 'inc'));

                        const isDragging = draggedCompId === comp.id;
                        const isDragTarget =
                          dragOverKey === comp.id && draggedCompId !== comp.id;

                        return (
                          <Box
                            key={comp.id}
                            data-comp-row={comp.id}
                            onDragOver={
                              draggedCompId
                                ? (e) => {
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = 'move';
                                    setDragOverKey(comp.id);
                                  }
                                : undefined
                            }
                            onDragLeave={
                              draggedCompId
                                ? () =>
                                    setDragOverKey((k) =>
                                      k === comp.id ? null : k,
                                    )
                                : undefined
                            }
                            onDrop={
                              draggedCompId
                                ? (e) => {
                                    e.preventDefault();
                                    handleComponentDrop(svc.id, comp.id);
                                  }
                                : undefined
                            }
                            sx={{
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              opacity: isDragging ? 0.4 : 1,
                              // Insertion line above the hovered target row
                              // (drop inserts before it).
                              boxShadow: isDragTarget
                                ? (theme) =>
                                    `inset 0 2px 0 0 ${theme.palette.primary.main}`
                                : 'none',
                              ...rowDecorative,
                            }}
                          >
                            {/* ---- main row ---- */}
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: GRID_TEMPLATE,
                                columnGap: 1,
                                // Top-align so every field's underline shares
                                // one baseline — the Item cell is taller (it
                                // stacks description + code), so centring would
                                // drop the single-line fields below the
                                // description's underline.
                                alignItems: 'start',
                                px: 1,
                                py: 0.75,
                                // One font size for every input so their
                                // heights — and therefore underlines — match.
                                // The code subtext keeps its own smaller inline
                                // size (inline style wins over this rule).
                                '& .MuiInputBase-input': { fontSize: '0.85rem' },
                              }}
                            >
                              {/* Drag handle — grab to reorder within the
                                  service. Only the handle is draggable so the
                                  row's inputs stay normally selectable. */}
                              <Box
                                draggable={!disabled}
                                onDragStart={(e) => {
                                  e.dataTransfer.effectAllowed = 'move';
                                  e.dataTransfer.setData('text/plain', comp.id);
                                  // Use the whole row as the drag image rather
                                  // than just the small handle.
                                  const row = (
                                    e.currentTarget as HTMLElement
                                  ).closest('[data-comp-row]');
                                  if (row) {
                                    e.dataTransfer.setDragImage(
                                      row as HTMLElement,
                                      0,
                                      0,
                                    );
                                  }
                                  // Defer the state flip a frame — a sync
                                  // re-render during dragstart can cancel the
                                  // native drag (same guard as the dashboard).
                                  requestAnimationFrame(() =>
                                    setDraggedCompId(comp.id),
                                  );
                                }}
                                onDragEnd={() => {
                                  setDraggedCompId(null);
                                  setDragOverKey(null);
                                }}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  alignSelf: 'stretch',
                                  color: 'text.disabled',
                                  cursor: disabled ? 'default' : 'grab',
                                  '&:active': {
                                    cursor: disabled ? 'default' : 'grabbing',
                                  },
                                  '&:hover': {
                                    color: disabled
                                      ? 'text.disabled'
                                      : 'text.secondary',
                                  },
                                }}
                              >
                                <DragIndicatorIcon sx={{ fontSize: '1.1rem' }} />
                              </Box>

                              {/* Kind */}
                              <Box>
                                <TextField
                                  select
                                  variant="standard"
                                  size="small"
                                  fullWidth
                                  aria-label="Kind"
                                  value={comp.kind}
                                  onChange={(e) => {
                                    const nextKind = e.target
                                      .value as RepairServiceComponent['kind'];
                                    const reseed = seedComponent(nextKind);
                                    updateComponent(svc.id, comp.id, {
                                      kind: nextKind,
                                      productCode: reseed.productCode,
                                      units: reseed.units,
                                      unitPriceExGst: reseed.unitPriceExGst,
                                      buyPriceExGst: reseed.buyPriceExGst,
                                      shipping: reseed.shipping,
                                      url: reseed.url,
                                      vendorId: reseed.vendorId ?? null,
                                      vendorName: reseed.vendorName ?? null,
                                      markupType: reseed.markupType ?? null,
                                      markupValue: reseed.markupValue ?? 0,
                                      leadTime: reseed.leadTime ?? null,
                                      supplierGstMode: reseed.supplierGstMode,
                                      fulfillmentSource:
                                        reseed.fulfillmentSource,
                                    });
                                  }}
                                  disabled={disabled}
                                  InputProps={{ disableUnderline: true }}
                                  SelectProps={{
                                    renderValue: () => (
                                      <Box
                                        component="span"
                                        sx={{
                                          display: 'inline-block',
                                          fontSize: '0.65rem',
                                          fontWeight: 700,
                                          letterSpacing: '0.03em',
                                          textTransform: 'uppercase',
                                          px: 0.75,
                                          py: '2px',
                                          borderRadius: 1,
                                          bgcolor: kindMeta.bg,
                                          color: kindMeta.fg,
                                        }}
                                      >
                                        {kindMeta.label}
                                      </Box>
                                    ),
                                  }}
                                >
                                  {COMPONENT_KINDS.map((k) => (
                                    <MenuItem key={k.value} value={k.value}>
                                      {k.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Box>

                              {/* Item: description + product-code subtext */}
                              <Box sx={{ minWidth: 0 }}>
                                <TextField
                                  variant="standard"
                                  size="small"
                                  fullWidth
                                  aria-label={
                                    isFixedPriceOnly && isFixedPriceComp
                                      ? 'Customer-facing description'
                                      : 'Description'
                                  }
                                  placeholder="Description"
                                  value={comp.description}
                                  onChange={(e) => {
                                    const next = e.target.value;
                                    updateComponent(svc.id, comp.id, {
                                      description: next,
                                    });
                                    if (isFixedPriceOnly && isFixedPriceComp) {
                                      updateService(svc.id, {
                                        description: next || null,
                                      });
                                    }
                                  }}
                                  disabled={disabled}
                                  InputProps={{
                                    sx: {
                                      fontSize: '0.85rem',
                                      fontWeight: 500,
                                    },
                                  }}
                                />
                                <TextField
                                  variant="standard"
                                  size="small"
                                  fullWidth
                                  aria-label="Product code"
                                  value={comp.productCode ?? ''}
                                  onChange={(e) => {
                                    const nextCode = e.target.value || null;
                                    if (isLabour) {
                                      const newRate =
                                        labourRateForSku(nextCode);
                                      const u = effectiveUnits(comp);
                                      const total = round2(u * newRate);
                                      updateComponent(svc.id, comp.id, {
                                        productCode: nextCode,
                                        unitPriceExGst: newRate,
                                        buyPriceExGst: total,
                                        sellPrice: total,
                                      });
                                    } else if (isFixedPriceComp) {
                                      const product = getProductBySku(nextCode);
                                      const exPrice = catalogIncToExGst(
                                        product?.unitCost,
                                      );
                                      updateComponent(svc.id, comp.id, {
                                        productCode: nextCode,
                                        sellPrice: exPrice,
                                      });
                                    } else {
                                      updateComponent(svc.id, comp.id, {
                                        productCode: nextCode,
                                      });
                                    }
                                  }}
                                  placeholder={defaultCode}
                                  disabled={disabled}
                                  InputProps={{ disableUnderline: true }}
                                  inputProps={{
                                    style: {
                                      fontSize: '0.7rem',
                                      fontFamily: 'monospace',
                                      padding: 0,
                                    },
                                  }}
                                  sx={{
                                    '& input::placeholder': {
                                      fontSize: '0.7rem',
                                    },
                                    '& input': { color: 'text.secondary' },
                                  }}
                                />
                              </Box>

                              {/* Qty / Hrs */}
                              <Box sx={{ textAlign: 'right' }}>
                                {isLabour || comp.kind === 'part' ? (
                                  <TextField
                                    variant="standard"
                                    size="small"
                                    fullWidth
                                    aria-label={isLabour ? 'Hours' : 'Qty'}
                                    type="number"
                                    value={round2(unitsValue)}
                                    onChange={(e) =>
                                      updateComponentNumeric(svc.id, comp.id, {
                                        units: round2(
                                          Number(e.target.value) || 0,
                                        ),
                                      })
                                    }
                                    disabled={disabled}
                                    inputProps={{
                                      step: isLabour ? '0.25' : '1',
                                      min: 0,
                                      style: { textAlign: 'right' },
                                    }}
                                  />
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.disabled"
                                  >
                                    —
                                  </Typography>
                                )}
                              </Box>

                              {/* Rate (labour) / Source (part) */}
                              <Box>
                                {isLabour ? (
                                  <TextField
                                    variant="standard"
                                    size="small"
                                    fullWidth
                                    aria-label={
                                      gstInclusive
                                        ? 'Rate inc-GST / hr'
                                        : 'Rate ex-GST / hr'
                                    }
                                    type="number"
                                    value={round2(
                                      gstInclusive
                                        ? unitPriceValue * 1.1
                                        : unitPriceValue,
                                    )}
                                    onChange={(e) => {
                                      const raw = Number(e.target.value) || 0;
                                      const ex = gstInclusive
                                        ? raw / 1.1
                                        : round2(raw);
                                      updateComponentNumeric(svc.id, comp.id, {
                                        unitPriceExGst: ex,
                                      });
                                    }}
                                    disabled={disabled}
                                    inputProps={{ step: '0.01', min: 0 }}
                                    InputProps={{
                                      endAdornment: (
                                        <Typography
                                          component="span"
                                          sx={{
                                            fontSize: '0.625rem',
                                            color: 'text.disabled',
                                            ml: 0.5,
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          /hr
                                        </Typography>
                                      ),
                                    }}
                                  />
                                ) : comp.kind === 'part' ? (
                                  <TextField
                                    select
                                    variant="standard"
                                    size="small"
                                    fullWidth
                                    aria-label="Source"
                                    value={comp.fulfillmentSource ?? 'order'}
                                    onChange={(e) =>
                                      updateComponent(svc.id, comp.id, {
                                        fulfillmentSource: e.target.value as
                                          | 'stock'
                                          | 'order',
                                      })
                                    }
                                    disabled={disabled}
                                    inputRef={(el) => {
                                      if (
                                        el &&
                                        pendingFocusSourceId === comp.id
                                      ) {
                                        el.focus();
                                        setPendingFocusSourceId(null);
                                      }
                                    }}
                                  >
                                    <MenuItem value="order">Order</MenuItem>
                                    <MenuItem value="stock">In stock</MenuItem>
                                  </TextField>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.disabled"
                                  >
                                    —
                                  </Typography>
                                )}
                              </Box>

                              {/* Sell */}
                              <Box sx={{ textAlign: 'right' }}>
                                <SellPriceInput
                                  label={
                                    sellGstInc
                                      ? 'Sell inc-GST'
                                      : 'Sell ex-GST'
                                  }
                                  valueExGst={comp.sellPrice}
                                  gstInclusive={sellGstInc}
                                  gstTag={
                                    comp.kind === 'part'
                                      ? sellGstInc
                                        ? 'inc'
                                        : 'ex'
                                      : undefined
                                  }
                                  disabled={disabled}
                                  onChange={(nextExGst) =>
                                    updateComponent(svc.id, comp.id, {
                                      sellPrice: nextExGst,
                                    })
                                  }
                                />
                              </Box>

                              {/* GP — margin %, amber when below floor */}
                              <Box sx={{ textAlign: 'right', pt: '5px' }}>
                                {verdict && verdict.margin !== null ? (
                                  <Tooltip
                                    title={
                                      showWarning
                                        ? `Margin ${fmtPct(
                                            verdict.margin,
                                          )} — below the ${fmtPct(
                                            verdict.required,
                                          )} floor for a ${fmtCurrency(
                                            lineCost,
                                          )} line. Profit ${fmtCurrency(
                                            verdict.profitExGst,
                                          )} ex-GST.`
                                        : `Profit ${fmtCurrency(
                                            verdict.profitExGst,
                                          )} ex-GST${
                                            comp.kind === 'part'
                                              ? ` · floor ${fmtPct(
                                                  verdict.required,
                                                )}`
                                              : ''
                                          }`
                                    }
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.25,
                                        justifyContent: 'flex-end',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        color: showWarning
                                          ? 'warning.main'
                                          : 'success.main',
                                      }}
                                    >
                                      {showWarning && (
                                        <WarningIcon
                                          sx={{ fontSize: '0.95rem' }}
                                        />
                                      )}
                                      {fmtPct(verdict.margin)}
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.disabled"
                                  >
                                    —
                                  </Typography>
                                )}
                              </Box>

                              {/* Delete */}
                              <Box sx={{ textAlign: 'right', mt: '-2px' }}>
                                <Tooltip title="Remove component">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        removeComponent(svc.id, comp.id)
                                      }
                                      disabled={disabled}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </Box>

                            {/* ---- detail row (sourcing / vendor quote) ---- */}
                            {hasDetailRow && (
                              <Box sx={{ px: 1, pb: 0.75 }}>
                                <Box
                                  onClick={() =>
                                    !detailOpen && toggleDetail(comp.id)
                                  }
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    cursor: detailOpen
                                      ? 'default'
                                      : 'pointer',
                                    color: showWarning
                                      ? 'warning.main'
                                      : 'text.secondary',
                                    // Match the always-visible row's input
                                    // font size so the collapsed Sourcing
                                    // summary reads at the same scale.
                                    fontSize: '0.85rem',
                                    minWidth: 0,
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleDetail(comp.id);
                                    }}
                                    sx={{ p: 0.25 }}
                                  >
                                    {detailOpen ? (
                                      <ExpandMoreIcon fontSize="small" />
                                    ) : (
                                      <ChevronRightIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                  <Box component="span" sx={{ fontWeight: 600 }}>
                                    {isThirdPartyComp
                                      ? 'Vendor quote'
                                      : 'Sourcing'}
                                  </Box>
                                  {!detailOpen && (
                                    <Box
                                      component="span"
                                      sx={{
                                        color: 'text.disabled',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        minWidth: 0,
                                      }}
                                    >
                                      {(() => {
                                        const vname =
                                          comp.vendorName || 'no vendor';
                                        const cost = fmtCurrency(
                                          comp.buyPriceExGst || 0,
                                        );
                                        const profit =
                                          verdict && !isThirdPartyComp
                                            ? ` · profit ${fmtCurrency(
                                                verdict.profitExGst,
                                              )}`
                                            : '';
                                        return ` — ${vname} · cost ${cost}${profit}`;
                                      })()}
                                    </Box>
                                  )}
                                </Box>

                                {detailOpen && (
                                  <Box
                                    sx={{
                                      pt: 1,
                                      pl: 3.5,
                                      // Match the always-visible row's input
                                      // scale for the expanded sourcing /
                                      // vendor-quote fields and their labels.
                                      '& .MuiInputBase-input': {
                                        fontSize: '0.85rem',
                                      },
                                      '& .MuiFormLabel-root': {
                                        fontSize: '0.8rem',
                                      },
                                    }}
                                  >
                                    {comp.kind === 'part'
                                      ? renderPartSourcing(svc, comp)
                                      : renderThirdPartyDetail(
                                          svc,
                                          comp,
                                          leadTimeValue,
                                          leadTimeValid,
                                        )}
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })}

                      {/* Drop zone for placing a dragged row at the bottom of
                          the service. Only shown while dragging a row that
                          belongs to this service. */}
                      {draggedCompId &&
                        svc.components.some(
                          (c) => c.id === draggedCompId,
                        ) && (
                          <Box
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = 'move';
                              setDragOverKey(`end::${svc.id}`);
                            }}
                            onDragLeave={() =>
                              setDragOverKey((k) =>
                                k === `end::${svc.id}` ? null : k,
                              )
                            }
                            onDrop={(e) => {
                              e.preventDefault();
                              handleComponentDrop(svc.id, null);
                            }}
                            sx={{
                              height: 22,
                              m: 0.5,
                              borderRadius: 1,
                              border: '2px dashed',
                              borderColor:
                                dragOverKey === `end::${svc.id}`
                                  ? 'primary.main'
                                  : 'transparent',
                              backgroundColor:
                                dragOverKey === `end::${svc.id}`
                                  ? 'action.hover'
                                  : 'transparent',
                              transition: 'all 0.15s ease',
                            }}
                          />
                        )}

                      {/* Per-service profit summary across part + accepted
                          third-party lines (labour / fixed excluded — no
                          separable cost). */}
                      {(() => {
                        const p = serviceProfit(svc);
                        if (!p.hasData) return null;
                        const factor = gstInclusive ? 1.1 : 1;
                        return (
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: 1.5,
                              px: 1,
                              py: 1,
                              fontSize: '0.8rem',
                            }}
                          >
                            {p.lowCount > 0 && (
                              <Chip
                                size="small"
                                color="warning"
                                icon={<WarningIcon />}
                                label={`${p.lowCount} line${
                                  p.lowCount === 1 ? '' : 's'
                                } below margin floor`}
                              />
                            )}
                            <Box sx={{ flex: 1 }} />
                            <Box component="span" sx={{ color: 'text.secondary' }}>
                              Parts &amp; vendor: cost{' '}
                              {fmtCurrency(p.costExGst * factor)} · profit{' '}
                              <Box
                                component="span"
                                sx={{
                                  fontWeight: 700,
                                  color:
                                    p.profitExGst < 0
                                      ? 'error.main'
                                      : 'success.main',
                                }}
                              >
                                {fmtCurrency(p.profitExGst * factor)}
                              </Box>
                              {p.margin !== null && (
                                <> · margin {fmtPct(p.margin)}</>
                              )}
                            </Box>
                          </Box>
                        );
                      })()}
                    </Box>
                  </Box>
                )}

                {/* Repeat the add-component buttons below a non-empty list so
                    the tech doesn't have to scroll back up to add another. */}
                {svc.components.length > 0 && (
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="flex-end"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {addComponentButtons}
                  </Stack>
                )}
              </Stack>
            </Box>
          </Stack>
        </Paper>
        );
      })}

      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ pt: 1 }}
      >
        {/* Locked mode bills a single fixed service, so the add-service
            picker is hidden — only the totals remain on this row. */}
        {!lockedServiceCode && (
          <Box sx={{ flex: 1, maxWidth: 360 }}>
            <RepairServicePicker
              value=""
              deviceType={deviceType}
              disabled={disabled}
              onPick={addService}
            />
          </Box>
        )}
        {!lockedServiceCode && (
          <Tooltip title="Add a product line billed separately, outside any repair service (e.g. a 1TB HDD on a data-recovery job)">
            <Button
              startIcon={<PartIcon />}
              variant="outlined"
              onClick={() => setStandalonePartPickerOpen(true)}
              disabled={disabled}
            >
              Add standalone part
            </Button>
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <Stack alignItems="flex-end" spacing={0.25}>
          <Typography variant="body2">
            Services total:{' '}
            <strong>{fmtCurrency(withEft(grandTotal))}</strong>{' '}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
            >
              {gstInclusive ? 'inc GST' : 'ex GST'}
              {eftActive ? ' · incl. EFT' : ''}
            </Typography>
          </Typography>
          {eftActive && (
            <Typography variant="caption" color="text.secondary">
              Totals include the EFT buffer (the price quoted to the customer);
              per-line prices and margins are shown pre-buffer.
            </Typography>
          )}
          {grandProfit.hasData && (
            <Typography variant="caption" color="text.secondary">
              Parts &amp; vendor profit:{' '}
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  color:
                    grandProfit.profit < 0 ? 'error.main' : 'success.main',
                }}
              >
                {fmtCurrency(grandProfit.profit * (gstInclusive ? 1.1 : 1))}
              </Box>
              {grandProfit.margin !== null && (
                <> · margin {fmtPct(grandProfit.margin)}</>
              )}
              {grandProfit.lowCount > 0 && (
                <Box component="span" sx={{ color: 'warning.main', ml: 0.5 }}>
                  · {grandProfit.lowCount} below floor
                </Box>
              )}
            </Typography>
          )}
        </Stack>
      </Stack>

      <InventoryPickerDialog
        open={partPickerServiceId !== null}
        onClose={() => setPartPickerServiceId(null)}
        quoteType="repair"
        onPick={(product) => {
          if (partPickerServiceId) {
            addPartFromInventory(partPickerServiceId, product);
          }
        }}
      />

      <InventoryPickerDialog
        open={standalonePartPickerOpen}
        onClose={() => setStandalonePartPickerOpen(false)}
        quoteType="repair"
        onPick={(product) => addStandalonePart(product)}
      />
    </Stack>
  );
};
